/**
 * Component-specific questions that appear when a user selects certain garden components
 * All questions are optional - users can skip and let AI decide
 */

import type { AIGeneratedQuestion } from './publicPlans';

export type ComponentQuestionConfig = {
	componentId: string;
	/** Display name for this component (used for gate question and section title). */
	label: string;
	questions: AIGeneratedQuestion[];
};

/**
 * Questions for each garden component
 * These are shown when the component is selected in step 2
 */
export const componentQuestions: ComponentQuestionConfig[] = [
	{
		componentId: 'pool',
		label: 'Pool / Swimming Pool',
		questions: [
			{
				question: 'What type of pool do you want?',
				type: 'multiple_choice',
				options: ['In-ground', 'Above-ground', 'Inflatable', 'Infinity edge', 'Natural swimming pool'],
				priority: 'low',
				reasoning: 'Pool type affects feasibility, cost, and installation complexity in smaller spaces.'
			},
			{
				question: 'What size pool are you interested in?',
				type: 'multiple_choice',
				options: ['Small (3-5m)', 'Medium (5-8m)', 'Large (8-12m)', 'Very Large (12m+)'],
				priority: 'low',
				reasoning: 'Pool size affects layout, cost, and available space for other features.'
			},
			{
				question: 'What pool shape do you prefer?',
				type: 'multiple_choice',
				options: ['Rectangular', 'Oval', 'Freeform', 'L-shaped', 'Round'],
				priority: 'low',
				reasoning: 'Shape affects aesthetics and how the pool integrates with your garden design.'
			},
			{
				question: 'What tile color/style do you prefer for the pool?',
				type: 'multiple_choice',
				options: ['Blue', 'White', 'Light Blue', 'Turquoise', 'Dark Blue', 'Natural Stone'],
				priority: 'low',
				reasoning: 'Tile color affects the overall aesthetic and can complement your garden style.'
			},
			{
				question: 'Do you want a pool deck/patio area?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'A deck or patio around the pool provides space for lounging and entertaining.'
			}
		]
	},
	{
		componentId: 'fence',
		label: 'Fence / Privacy Fence',
		questions: [
			{
				question: 'What fence height do you need?',
				type: 'multiple_choice',
				options: ['Low (1-1.5m)', 'Medium (1.5-2m)', 'High (2m+)'],
				priority: 'low',
				reasoning: 'Height affects privacy, security, and visual impact.'
			},
			{
				question: 'What fence material/style do you prefer?',
				type: 'multiple_choice',
				options: ['Wood', 'Metal', 'Vinyl', 'Bamboo', 'Stone/Brick'],
				priority: 'low',
				reasoning: 'Material affects durability, maintenance, and aesthetic.'
			},
			{
				question: 'What color/finish do you prefer?',
				type: 'multiple_choice',
				options: ['Natural', 'Stained', 'Painted', 'Black', 'White'],
				priority: 'low',
				reasoning: 'Color affects how the fence complements your garden and house.'
			}
		]
	},
	{
		componentId: 'bbq',
		label: 'BBQ / Grill Area',
		questions: [
			{
				question: 'What type of BBQ/Grill do you want?',
				type: 'multiple_choice',
				options: ['Built-in', 'Portable', 'Gas', 'Charcoal', 'Pellet'],
				priority: 'low',
				reasoning: 'Type affects installation requirements and cooking experience.'
			},
			{
				question: 'Do you want a covered BBQ area?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Cover provides protection from weather and allows year-round use.'
			},
			{
				question: 'What size BBQ area do you need?',
				type: 'multiple_choice',
				options: ['Small (just grill)', 'Medium (grill + prep)', 'Large (grill + prep + seating)'],
				priority: 'low',
				reasoning: 'Size affects layout and space allocation.'
			}
		]
	},
	{
		componentId: 'lounge',
		label: 'Lounge / Seating Area',
		questions: [
			{
				question: 'What type of seating do you prefer?',
				type: 'multiple_choice',
				options: ['Outdoor Sofa Set', 'Dining Set', 'Lounge Chairs', 'Mixed'],
				allowMultiple: true,
				priority: 'low',
				reasoning: 'Seating type affects layout and functionality.'
			},
			{
				question: 'Do you want a covered/secluded area?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Cover provides shade and protection, creating a more intimate space.'
			}
		]
	},
	{
		componentId: 'outdoor-kitchen',
		label: 'Outdoor Kitchen',
		questions: [
			{
				question: 'What appliances do you want in your outdoor kitchen?',
				type: 'multiple_choice',
				options: ['Just BBQ/Grill', 'BBQ + Sink', 'BBQ + Sink + Fridge', 'Full Kitchen'],
				priority: 'low',
				reasoning: 'Appliances affect space requirements and plumbing/electrical needs.'
			},
			{
				question: 'What countertop material do you prefer?',
				type: 'multiple_choice',
				options: ['Granite', 'Concrete', 'Stainless Steel', 'Tile', 'Natural Stone'],
				priority: 'low',
				reasoning: 'Material affects durability, maintenance, and aesthetic.'
			}
		]
	},
	{
		componentId: 'fire-pit',
		label: 'Fire Pit / Fireplace',
		questions: [
			{
				question: 'What type of fire pit do you prefer?',
				type: 'multiple_choice',
				options: ['Wood-burning', 'Gas', 'Propane'],
				priority: 'low',
				reasoning: 'Type affects installation, fuel requirements, and maintenance.'
			},
			{
				question: 'What shape do you prefer?',
				type: 'multiple_choice',
				options: ['Round', 'Square', 'Rectangular'],
				priority: 'low',
				reasoning: 'Shape affects seating arrangement and aesthetic.'
			},
			{
				question: 'What material do you prefer?',
				type: 'multiple_choice',
				options: ['Stone', 'Metal', 'Concrete', 'Brick'],
				priority: 'low',
				reasoning: 'Material affects durability and aesthetic.'
			}
		]
	},
	{
		componentId: 'pergola',
		label: 'Pergola / Pavilion',
		questions: [
			{
				question: 'What size pergola do you need?',
				type: 'multiple_choice',
				options: ['Small (3x3m)', 'Medium (4x4m)', 'Large (5x5m+)', 'Custom Size'],
				priority: 'low',
				reasoning: 'Size affects space allocation and cost.'
			},
			{
				question: 'What material do you prefer?',
				type: 'multiple_choice',
				options: ['Wood', 'Metal', 'Vinyl', 'Composite'],
				priority: 'low',
				reasoning: 'Material affects durability, maintenance, and aesthetic.'
			},
			{
				question: 'Do you want a retractable canopy or roof?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Canopy provides adjustable shade and weather protection.'
			}
		]
	},
	{
		componentId: 'deck',
		label: 'Deck / Patio (Terrace)',
		questions: [
			{
				question: 'What deck material do you prefer?',
				type: 'multiple_choice',
				options: ['Wood', 'Composite', 'PVC', 'Aluminum'],
				priority: 'low',
				reasoning: 'Material affects durability, maintenance, and cost.'
			},
			{
				question: 'What color/finish do you prefer?',
				type: 'multiple_choice',
				options: ['Natural Wood', 'Stained', 'Gray', 'Brown'],
				priority: 'low',
				reasoning: 'Color affects aesthetic and how it complements your house and garden.'
			},
			{
				question: 'What deck height do you need?',
				type: 'multiple_choice',
				options: ['Ground Level', 'Low (30-50cm)', 'Medium (50-100cm)', 'High (100cm+)'],
				priority: 'low',
				reasoning: 'Height affects access, views, and integration with the garden.'
			}
		]
	},
	{
		componentId: 'shed',
		label: 'Garden Shed / Storage',
		questions: [
			{
				question: 'What size shed do you need?',
				type: 'multiple_choice',
				options: ['Small (2x2m)', 'Medium (3x3m)', 'Large (4x4m+)'],
				priority: 'low',
				reasoning: 'Size depends on what you plan to store.'
			},
			{
				question: 'What material do you prefer?',
				type: 'multiple_choice',
				options: ['Wood', 'Metal', 'Plastic'],
				priority: 'low',
				reasoning: 'Material affects durability, cost, and aesthetic.'
			},
			{
				question: 'What color do you prefer?',
				type: 'multiple_choice',
				options: ['Natural', 'Green', 'Brown', 'Gray', 'White'],
				priority: 'low',
				reasoning: 'Color affects how it blends with your garden.'
			}
		]
	},
	{
		componentId: 'playground',
		label: 'Play Area',
		questions: [
			{
				question: 'What type of play equipment do you want?',
				type: 'multiple_choice',
				options: ['Swings', 'Slide', 'Climbing', 'All of the above'],
				allowMultiple: true,
				priority: 'low',
				reasoning: 'Equipment type affects space requirements and layout.'
			},
			{
				question: 'Do you want a safety surface?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Safety surface (rubber, mulch, etc.) reduces injury risk from falls.'
			}
		]
	},
	{
		componentId: 'vegetable-garden',
		label: 'Vegetable Garden / Raised Beds',
		questions: [
			{
				question: 'What do you want to grow?',
				type: 'multiple_choice',
				options: ['Mostly vegetables', 'Mostly herbs & spices', 'Both vegetables and herbs'],
				priority: 'low',
				reasoning: 'Helps balance bed space between veg crops and culinary herbs.'
			},
			{
				question: 'What size vegetable garden do you want?',
				type: 'multiple_choice',
				options: ['Small (2-4m²)', 'Medium (4-8m²)', 'Large (8m²+)'],
				priority: 'low',
				reasoning: 'Size depends on how much produce you want to grow.'
			},
			{
				question: 'Do you prefer raised beds or in-ground?',
				type: 'multiple_choice',
				options: ['Raised Beds', 'In-Ground', 'Mixed'],
				priority: 'low',
				reasoning: 'Raised beds are easier on the back and better for drainage.'
			},
			{
				question: 'What materials do you prefer for raised beds?',
				type: 'multiple_choice',
				options: ['Wood', 'Metal', 'Stone', 'Composite'],
				priority: 'low',
				reasoning: 'Material affects durability, cost, and aesthetic.'
			}
		]
	},
	{
		componentId: 'water-feature',
		label: 'Water Feature (Fountain, Pond, Stream)',
		questions: [
			{
				question: 'What type of water feature do you want?',
				type: 'multiple_choice',
				options: ['Fountain', 'Pond', 'Waterfall', 'Stream'],
				allowMultiple: true,
				priority: 'low',
				reasoning: 'Type affects installation complexity and maintenance.'
			},
			{
				question: 'What size do you prefer?',
				type: 'multiple_choice',
				options: ['Small', 'Medium', 'Large'],
				priority: 'low',
				reasoning: 'Size affects space allocation and visual impact.'
			},
			{
				question: 'What material/style do you prefer?',
				type: 'multiple_choice',
				options: ['Natural Stone', 'Modern/Concrete', 'Ceramic'],
				priority: 'low',
				reasoning: 'Material affects aesthetic and maintenance.'
			}
		]
	},
	{
		componentId: 'lighting',
		label: 'Outdoor Lighting',
		questions: [
			{
				question: 'What type of lighting do you want?',
				type: 'multiple_choice',
				options: ['Pathway Lights', 'Spotlights', 'String Lights', 'Solar', 'LED', 'Mixed'],
				allowMultiple: true,
				priority: 'low',
				reasoning: 'Type affects ambiance, functionality, and installation.'
			},
			{
				question: 'What color temperature do you prefer?',
				type: 'multiple_choice',
				options: ['Warm White', 'Cool White', 'Color Changing'],
				priority: 'low',
				reasoning: 'Color temperature affects mood and ambiance.'
			}
		]
	},
	{
		componentId: 'pathway',
		label: 'Path / Walkway',
		questions: [
			{
				question: 'What pathway material do you prefer?',
				type: 'multiple_choice',
				options: ['Gravel', 'Stone', 'Pavers', 'Concrete', 'Wood'],
				priority: 'low',
				reasoning: 'Material affects durability, maintenance, and aesthetic.'
			},
			{
				question: 'What width do you prefer?',
				type: 'multiple_choice',
				options: ['Narrow (60cm)', 'Standard (90cm)', 'Wide (120cm+)'],
				priority: 'low',
				reasoning: 'Width affects accessibility and visual impact.'
			},
			{
				question: 'What pattern/style do you prefer?',
				type: 'multiple_choice',
				options: ['Straight', 'Curved', 'Stepping Stones'],
				priority: 'low',
				reasoning: 'Pattern affects flow and aesthetic.'
			}
		]
	},
	{
		componentId: 'retaining-wall',
		label: 'Retaining Wall',
		questions: [
			{
				question: 'What height retaining wall do you need?',
				type: 'multiple_choice',
				options: ['Low (30-50cm)', 'Medium (50-100cm)', 'High (100cm+)'],
				priority: 'low',
				reasoning: 'Height affects structural requirements and cost.'
			},
			{
				question: 'What material do you prefer?',
				type: 'multiple_choice',
				options: ['Stone', 'Concrete Blocks', 'Wood', 'Gabion'],
				priority: 'low',
				reasoning: 'Material affects durability, cost, and aesthetic.'
			},
			{
				question: 'What color/finish do you prefer?',
				type: 'multiple_choice',
				options: ['Natural', 'Stained', 'Painted'],
				priority: 'low',
				reasoning: 'Color affects how it blends with your garden.'
			}
		]
	},
	{
		componentId: 'driveway',
		label: 'Driveway / Parking Area',
		questions: [
			{
				question: 'What driveway surface do you prefer?',
				type: 'multiple_choice',
				options: ['Gravel', 'Pavers', 'Concrete', 'Resin-bound'],
				priority: 'low',
				reasoning: 'Surface affects durability, drainage, and aesthetic.'
			},
			{
				question: 'Do you want to preserve an existing driveway as-is?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Preserving existing saves cost and keeps current layout.'
			}
		]
	},
	{
		componentId: 'greenhouse',
		label: 'Greenhouse / Cold Frame',
		questions: [
			{
				question: 'What size greenhouse do you want?',
				type: 'multiple_choice',
				options: ['Small (2x3m)', 'Medium (3x4m)', 'Large (4x6m+)'],
				priority: 'low',
				reasoning: 'Size affects space allocation and cost.'
			},
			{
				question: 'What glazing do you prefer?',
				type: 'multiple_choice',
				options: ['Glass', 'Polycarbonate'],
				priority: 'low',
				reasoning: 'Glazing affects light, insulation, and cost.'
			}
		]
	},
	{
		componentId: 'flower-beds',
		label: 'Flower Beds & Borders',
		questions: [
			{
				question: 'What style of flower beds do you prefer?',
				type: 'multiple_choice',
				options: ['Borders along paths', 'Island beds', 'Mixed / Cottage', 'Formal'],
				priority: 'low',
				reasoning: 'Style affects layout and plant selection.'
			},
			{
				question: 'What colour palette do you prefer for flowers?',
				type: 'multiple_choice',
				options: [
					'Soft pastels',
					'Bold, vibrant colours',
					'White & green / mostly neutral',
					'Warm tones (yellows, oranges, reds)',
					'Cool tones (blues, purples, pinks)',
					'Natural mixed — suited to my climate',
					'No strong preference'
				],
				priority: 'low',
				reasoning: 'Colour direction for blooms and accents; species still follow climate and location.'
			},
			{
				question: 'What edging do you prefer?',
				type: 'multiple_choice',
				options: ['Stone', 'Brick', 'Metal', 'Wood', 'None / Natural'],
				priority: 'low',
				reasoning: 'Edging defines beds and affects maintenance.'
			}
		]
	},
	{
		componentId: 'hot-tub',
		label: 'Hot Tub / Jacuzzi',
		questions: [
			{
				question: 'Where do you want the hot tub?',
				type: 'multiple_choice',
				options: ['On deck/patio', 'In-ground', 'Raised surround'],
				priority: 'low',
				reasoning: 'Placement affects access and integration with the garden.'
			},
			{
				question: 'What type of hot tub/jacuzzi do you prefer?',
				type: 'multiple_choice',
				options: ['In-ground spa', 'Portable rigid-shell', 'Inflatable', 'Deck-integrated'],
				priority: 'low',
				reasoning: 'Type affects required space, installation effort, and budget.'
			},
			{
				question: 'Do you want a covered or open area around it?',
				type: 'multiple_choice',
				options: ['Open', 'Pergola above', 'Privacy screen'],
				priority: 'low',
				reasoning: 'Cover affects privacy and year-round use.'
			}
		]
	},
	{
		componentId: 'pizza-oven',
		label: 'Pizza Oven',
		questions: [
			{
				question: 'What type of pizza oven do you want?',
				type: 'multiple_choice',
				options: ['Built-in wood-fired', 'Portable', 'Gas-assisted'],
				priority: 'low',
				reasoning: 'Type affects installation and cooking experience.'
			},
			{
				question: 'Do you want a prep counter or seating nearby?',
				type: 'yes_no',
				priority: 'low',
				reasoning: 'Prep and seating improve usability.'
			}
		]
	},
	{
		componentId: 'compost',
		label: 'Compost Area',
		questions: [
			{
				question: 'What type of compost area do you prefer?',
				type: 'multiple_choice',
				options: ['Bin(s)', 'Bay / three-bay', 'Tumbler', 'Hidden / screened'],
				priority: 'low',
				reasoning: 'Type affects capacity and how visible it is in the garden.'
			}
		]
	}
];

/**
 * Get questions for selected components.
 * Each component gets a small group of detail questions about that component.
 */
export function getComponentQuestions(selectedComponents: string[]): AIGeneratedQuestion[] {
	const questions: AIGeneratedQuestion[] = [];

	selectedComponents.forEach(componentId => {
		const config = componentQuestions.find(cq => cq.componentId === componentId);
		if (!config) return;

		const group = config.componentId;

		// Detail questions for this component
		config.questions.forEach(q => {
			questions.push({
				...q,
				group
			});
		});
	});

	return questions;
}
